import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { User, UserManager } from 'oidc-client';
import { BehaviorSubject, Observable, throwError as _throw } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UserModel } from 'app/models/user.model';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { MVLocalStorageService } from './mv-local-storage.service';
import moment from 'moment';
import { navigation } from 'app/navigation/navigation';
import { ConfigsLoaderService } from './config-loader.service';
import { AppRoutePaths } from 'app/enums/routes.enum';
import { PermissionType } from 'app/enums/permission.enum';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public currentUser$: Observable<UserModel>;
  private apiEndPoint: string;
  private applicationId: number;
  private currentUserSubject: BehaviorSubject<UserModel>;
  private manager: UserManager;
  private identityToken: User = null;


  constructor(
    private http: HttpClient,
    public jwtHelperService: JwtHelperService,
    private translate: TranslateService,
    private router: Router,
    private config: ConfigsLoaderService,
  ) {
    this.manager = new UserManager(this.config.auth);
    // Initialize the service
    this._init();
  }

  public get redirectUrl(): string {
    return MVLocalStorageService.getItem('__redirect__');
  }

  public set redirectUrl(value) {
    MVLocalStorageService.setItem('__redirect__', value);
  }

  get currentUser(): UserModel {
    return this.currentUserSubject.value;
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get currentUserName(): string {
    try {
      return this.currentUser.userName;
    } catch {
      return 'N/A';
    }
  }

  tryToAuthorizeUser(): void {
    if (!this.currentUser) {
      // tslint:disable-next-line: no-string-literal
      this.login(this.getClaims()[this.config.cloudEmailKey])
        .subscribe();
    }
  }

  // tslint:disable-next-line: no-any
  getClaims(): any {
    return this.identityToken.profile;
  }

  userHasPermission(permission: PermissionType): boolean {
    return this.currentUser.permissions.map(x => x.id).includes(permission as number);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  // tslint:disable-next-line: no-any
  ping(ping: number): Observable<any> {
    return this.http
      .get(`${this.apiEndPoint}/authentication/ping/${ping}`);
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------
  // tslint:disable-next-line: no-any
  login(email: string): Observable<any> {
    const minutesFromUTC = moment().utcOffset();
    const bearer = this.getAuthorizationHeaderValue();
    const applicationId = this.applicationId;

    const headers = new HttpHeaders({ 'Authorization': bearer }); // 'StaticAuthorization' : key,
    return this.http
      // tslint:disable-next-line: no-any
      .post<UserModel>(`${this.apiEndPoint}/Authentication/Authorize`, { email, minutesFromUTC, applicationId }, { headers })
      .pipe(
        map(user => {
          MVLocalStorageService.setItem('current_user', JSON.stringify(user));
          this.currentUserSubject.next(user);

          if (this.redirectUrl && this.redirectUrl != '/') {

            this.router.navigate([this.redirectUrl]);
          } else {
            this.redirectUrl = AppRoutePaths.HOME;

            this.router.navigate([this.redirectUrl]);
          }
          return user;
        }),
        catchError(err => {
          return _throw(err);
        })
      );
  }

  logout(): void {
    if (this.currentUser) {
      const applicationId = this.applicationId;
      let headers = new HttpHeaders({
        Authorization: this.getAuthorizationHeaderValue()
      })

      this.http.post(`${this.apiEndPoint}/authentication/logout`, { headers: headers })
        .pipe(
          map(res => {
            this.clearUserData();
            this.manager.signoutRedirect();
          }),
          catchError(err => {
            this.clearUserData();
            if (err.errorcode) {
              return _throw(err.errorcode);
            }
            //this.router.navigate(['/unauthorized']);
            this.router.navigateByUrl(AppRoutePaths.FORBIDDEN);
          })
        ).subscribe();
    }
    else {
      this.clearUserData();
      this.manager.signoutRedirect();
    }
  }

  clearUserData() {
    // remove user from local storage to log user out
    MVLocalStorageService.removeItem('current_user');
    MVLocalStorageService.removeSessionItem('ticketlist_fitlerForm');
    MVLocalStorageService.removeSessionItem('ticketlist_sortOrder');
    MVLocalStorageService.removeSessionItem('ticketlist_sortBy');
    MVLocalStorageService.removeSessionItem('ticketlist_pageSize');
    MVLocalStorageService.removeItem('selected_tenant');

    MVLocalStorageService.removeSessionItem('ticketlist_fitlerForm');
    MVLocalStorageService.removeSessionItem('ticketlist_pageSize');
    MVLocalStorageService.removeSessionItem('ticketlist_sortOrder');
    MVLocalStorageService.removeSessionItem('ticketlist_sortBy');

    this.currentUserSubject.next(null);
  }

  isLoggedIn(): Promise<boolean> {
    return this.manager.getUser().then(user => {
      this.identityToken = user;

      return this.identityToken !== null && !this.identityToken.expired;
    });
  }

  isLoggedInAndAuthorized(): Promise<boolean> {
    return this.manager.getUser().then(user => {
      this.identityToken = user;
      return this.identityToken !== null && !this.identityToken.expired && !(!this.currentUser || !this.currentUser.permissions);
    });
  }

  isAuthorized(permissionId: number | number[]): boolean {
    if (!this.currentUser || !this.currentUser.permissions) {
      return false;
    }

    if (Array.isArray(permissionId))
      return this.currentUser.permissions.some(p => permissionId.includes(p.id));
    else
      return this.currentUser.permissions.some(p => p.id === permissionId);
  }

  getAuthorizationHeaderValue(): string {
    if (this.identityToken.profile.tokenName === 'access_token') {
      return `${this.identityToken.token_type} ${this.identityToken.access_token}`;
    } else if (this.identityToken.profile.tokenName === 'id_token') {
      return `${this.identityToken.token_type} ${this.identityToken.id_token}`;
    } else if (this.identityToken.profile.tokenName === 'refresh_token') {
      return `${this.identityToken.token_type} ${this.identityToken.refresh_token}`;
    } else if (this.identityToken.profile.tokenName === undefined) {
        if (this.config.accessTokenType === 'access_token') {
            return `${this.identityToken.token_type} ${this.identityToken.access_token}`;
        } else {
            return `${this.identityToken.token_type} ${this.identityToken.id_token}`;
        }
    }
    return `${this.identityToken.token_type} ${this.identityToken.access_token}`;
  }

  setTokenPreferences(user) {
    if (user && user.token) {
      MVLocalStorageService.setItem('current_user', JSON.stringify(user));
      this.currentUserSubject.next(user);
    }
  }

  enforceLogin(pMessage): void {
    this.clearUserData();
    if (pMessage === 'ErrInvalidToken') {
      this.manager.signoutRedirect();
    } else if (pMessage === 'ErrIncorrectEmail' || pMessage === 'ErrUserNotFound' || pMessage === 'DoNotHavePermissionsForThisApplication') {
      this.router.navigateByUrl(AppRoutePaths.FORBIDDEN);
    } else {
      this.router.navigate(['/logout-callback'], { queryParams: { errorMessage: 'La sesión expiró' } });
    }
  }

  getUserByEmailForRegister(email: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiEndPoint}/Authentication/GetUserByEmailForRegister/`, { email });
  }
  getUserByEmailForRecoveryPass(email: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiEndPoint}/Authentication/GetUserByEmailForRecoveryPass/`, { email })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError(err => {
          if (err) {
            return _throw(err);
          }
          return _throw(this.translate.instant('errors.ErrUnexpected'));
        })
      );
  }

  startAuthentication(): Promise<void> {
    return this.manager.signinRedirect();
  }

  completeAuthentication(): Promise<void> {
    return this.manager.signinRedirectCallback().then(user => {
      this.identityToken = user;
    });
  }
  checkFirstAllowedMenuItem() {
    let url = null;
    navigation.forEach(item => {
      if (!url) {
        if (this.isAuthorized(Number(item.id))) {
          url = item.url;
        }
      }
    });

    if (url) {
      this.router.navigate([url]);
    } else {
      this.router.navigate(['/forbidden']);
    }
  }

  private _init(): void {
    // Set initial value
    this.apiEndPoint = this.config.apiUrl;
    this.applicationId = this.config.applicationId;

    this.currentUserSubject = new BehaviorSubject<UserModel>(JSON.parse(MVLocalStorageService.getItem('current_user')));
    this.currentUser$ = this.currentUserSubject.asObservable();
  }
}
